import React, {useEffect, useMemo, useState} from 'react';
import {Alert, Button, Card, Empty, message, Spin, Table, Tabs} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import {PlusOutlined} from '@ant-design/icons';
import {useMutation, useQuery} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import {
  ComplaintOrgHubsResponse,
  ComplaintOrgSystemAdminsResponse,
  ComplaintOrgViewResponse,
  ComplaintOrgViewVariables,
  GET_COMPLAINT_ORG_HUBS,
  GET_COMPLAINT_ORG_SYSTEM_ADMINS,
  GET_COMPLAINT_ORG_VIEW,
} from '../../../graphql/complaintOrg/queries';
import {
  REMOVE_COMPLAINT_ORG_MEMBER,
  RemoveComplaintOrgMemberResponse,
} from '../../../graphql/complaintOrg/mutations';
import {
  ComplaintOrgMemberType,
  ComplaintOrgRoleCode,
  ComplaintOrgWarehouseRowType,
} from '../../../graphql/complaintOrg/types';
import {translateGraphQLError} from '../../../utils/graphqlUtils';
import MemberChip from './components/MemberChip';
import AddMemberModal, {AddMemberTarget} from './components/AddMemberModal';
import MemberDetailModal from './components/MemberDetailModal';

interface MemberListProps {
  members: ComplaintOrgMemberType[];
  onAdd: () => void;
  onSelect: (member: ComplaintOrgMemberType) => void;
}

/** 一个格子：chip 列表 + 「添加」；没人时显示「未配置」 */
const MemberList: React.FC<MemberListProps> = ({members, onAdd, onSelect}) => {
  const {t} = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-2">
      {members.length === 0 && <span className="text-gray-300 text-sm">{t('complaintOrg.notConfigured')}</span>}
      {members.map((m) => (
        <MemberChip key={m.memberId} member={m} onClick={onSelect}/>
      ))}
      <Button type="link" size="small" icon={<PlusOutlined/>} onClick={onAdd} className="!px-1">
        {t('complaintOrg.add')}
      </Button>
    </div>
  );
};

/**
 * QC → 客诉任务管理平台 →组织角色人员配置。
 *
 * 按「HUB / 仓库 × 角色」维护负责人名单：顶部系统管理员（全局），下方按 HUB 切换，
 * 展示 HUB 级 5 个角色与该 HUB 下各仓库的工单负责人。HUB / 仓库来自 warehouse 表，名单来自账户服务。
 */
const ComplaintOrgPage: React.FC = () => {
  const {t} = useTranslation();
  // 选中的 HUB 存 tab key（字符串）：Long 型 id 经 GraphQL 返回的是字符串，用 Number(key) 比较会永远对不上
  const [hubKey, setHubKey] = useState<string>();
  const [addTarget, setAddTarget] = useState<AddMemberTarget | null>(null);
  const [selected, setSelected] = useState<ComplaintOrgMemberType | null>(null);

  const {data: hubsData, loading: hubsLoading, error: hubsError} =
    useQuery<ComplaintOrgHubsResponse>(GET_COMPLAINT_ORG_HUBS);
  const hubs = useMemo(() => hubsData?.complaintOrgHubs ?? [], [hubsData]);
  const currentHub = hubs.find((h) => String(h.id) === hubKey);
  const hubId = currentHub?.id;

  // 无「全部」：默认选中第一个 HUB
  useEffect(() => {
    if (hubKey === undefined && hubs.length > 0) {
      setHubKey(String(hubs[0].id));
    }
  }, [hubKey, hubs]);

  // 系统管理员：独立查询，不随 HUB 切换、不参与搜索
  const {data: adminsData, refetch: refetchAdmins} = useQuery<ComplaintOrgSystemAdminsResponse>(
    GET_COMPLAINT_ORG_SYSTEM_ADMINS, {fetchPolicy: 'network-only'});

  const {data: viewData, loading: viewLoading, error: viewError, refetch: refetchView} =
    useQuery<ComplaintOrgViewResponse, ComplaintOrgViewVariables>(GET_COMPLAINT_ORG_VIEW, {
      variables: {hubId: hubId as number},
      skip: hubId === undefined,
      // 账号状态是实时查询的，不走缓存
      fetchPolicy: 'network-only',
    });
  const view = viewData?.complaintOrgView;

  // 增删后两块都刷新：成本低，免去按角色判断刷哪块
  const refetch = () => {
    refetchAdmins();
    if (hubId !== undefined) {
      refetchView();
    }
  };

  const [removeMember] = useMutation<RemoveComplaintOrgMemberResponse>(REMOVE_COMPLAINT_ORG_MEMBER);

  // 详情弹窗里确认后调用；失败时抛出让确认框保持打开
  const handleRemove = async (member: ComplaintOrgMemberType) => {
    try {
      await removeMember({variables: {memberId: member.memberId}});
      message.success(t('complaintOrg.removeSuccess'));
      refetch();
    } catch (e) {
      message.error(translateGraphQLError(e, 'complaintOrg.errors.removeFailed'));
      throw e;
    }
  };

  const hubLabel = currentHub ? t('complaintOrg.hubTitle', {name: currentHub.name}) : '';
  const openAdd = (roleCode: ComplaintOrgRoleCode, warehouse?: ComplaintOrgWarehouseRowType) =>
    setAddTarget({
      roleCode,
      hubId: roleCode === 'SYSTEM_ADMIN' ? undefined : hubId,
      warehouseId: warehouse?.warehouseId,
      scopeLabel: roleCode === 'SYSTEM_ADMIN'
        ? undefined
        : warehouse ? `${hubLabel} · ${warehouse.warehouseName}` : hubLabel,
    });

  const columns: ColumnsType<ComplaintOrgWarehouseRowType> = [
    {
      title: t('complaintOrg.columns.warehouse'),
      dataIndex: 'warehouseName',
      width: 180,
      render: (name: string) => <span className="text-purple-600">{name}</span>,
    },
    {
      title: t('complaintOrg.role.TICKET_OWNER'),
      key: 'ticketOwners',
      render: (_, row) => (
        <MemberList members={row.ticketOwners} onAdd={() => openAdd('TICKET_OWNER', row)} onSelect={setSelected}/>
      ),
    },
    {
      // 本期不做系统外临时仓，操作列统一为「—」
      title: t('complaintOrg.columns.action'),
      key: 'action',
      width: 100,
      render: () => <span className="text-gray-300">—</span>,
    },
  ];

  if (hubsError) {
    return <Alert type="error" showIcon message={translateGraphQLError(hubsError, 'complaintOrg.errors.loadFailed')}/>;
  }

  return (
    <div className="flex flex-col gap-4 overflow-auto">
      <Card size="small">
        <div className="flex items-center gap-6">
          <div className="font-medium border-l-4 border-purple-600 pl-2 w-28 shrink-0">
            {t('complaintOrg.role.SYSTEM_ADMIN')}
          </div>
          <MemberList
            members={adminsData?.complaintOrgSystemAdmins ?? []}
            onAdd={() => openAdd('SYSTEM_ADMIN')}
            onSelect={setSelected}
          />
        </div>
      </Card>

      <Spin spinning={hubsLoading}>
        {!hubsLoading && hubs.length === 0 ? (
          <Card><Empty description={t('complaintOrg.noHub')}/></Card>
        ) : (
          <Tabs
            activeKey={hubKey}
            onChange={setHubKey}
            items={hubs.map((h) => ({key: String(h.id), label: h.name}))}
          />
        )}
      </Spin>

      {viewError && (
        <Alert type="error" showIcon message={translateGraphQLError(viewError, 'complaintOrg.errors.loadFailed')}/>
      )}

      {currentHub && (
        <Spin spinning={viewLoading}>
          <Card
            size="small"
            title={<span className="border-l-4 border-purple-600 pl-2">{hubLabel}</span>}
            extra={<span className="text-gray-400">
              {t('complaintOrg.warehouseCount', {count: currentHub.warehouseCount})}
            </span>}
          >
            <div className="grid grid-cols-2 gap-x-8 gap-y-3 mb-4">
              {(view?.hubRoles ?? []).map((group) => (
                <div key={group.roleCode} className="flex items-center gap-4">
                  <div className="w-28 shrink-0">{t(`complaintOrg.role.${group.roleCode}`)}</div>
                  <MemberList
                    members={group.members}
                    onAdd={() => openAdd(group.roleCode)}
                    onSelect={setSelected}
                  />
                </div>
              ))}
            </div>
            <Table
              rowKey="warehouseId"
              size="middle"
              columns={columns}
              dataSource={view?.warehouses ?? []}
              pagination={false}
            />
          </Card>
        </Spin>
      )}

      <AddMemberModal target={addTarget} onClose={() => setAddTarget(null)} onAdded={() => refetch()}/>
      <MemberDetailModal member={selected} onClose={() => setSelected(null)} onRemove={handleRemove}/>
    </div>
  );
};

export default ComplaintOrgPage;
